/**
 * Show When — conditional form blocks
 * ========================================================================
 * Shows a block only while another control in the same form has a given
 * value. Replaces the per-template show/hide IIFE.
 *
 * Usage — put data-show-when on the block:
 *
 *   <div data-show-when="price_reduced=yes">...</div>       one value
 *   <div data-show-when="status=listed,pending">...</div>   any of these
 *   <div data-show-when="status!=closed">...</div>          anything but these
 *   <div data-show-when="has_hoa">...</div>                 checked / not blank
 *
 * The name is the controlling field's `name`. It works with radios,
 * selects (including multiple), checkbox groups, and text inputs. A
 * checkbox without a value attribute posts "on".
 *
 * While a block is hidden:
 *   - it gets the `hidden` attribute
 *   - every input/select/textarea/button inside is disabled, so it never posts
 *     and its `required` never blocks submit. Fields the template already
 *     disabled stay disabled when the block shows again.
 *
 * Hiding a field does not clear what is already saved. The server should
 * blank dependent fields when the controlling answer rules them out.
 *
 * Blocks can nest; a block inside a hidden block is hidden too. A
 * controlling field inside a hidden block counts as unanswered. One
 * condition per block — nest blocks for AND.
 *
 * Re-checks on change/input, form reset, htmx:afterSettle, dropdown:open, sheet:ready,
 * and the combobox/typeahead/tag-input select events. SeeMe.showWhen.refresh() re-checks everything by hand.
 */

var SeeMe = window.SeeMe || {};

(function () {
    var ATTR = 'data-show-when';
    var MARK = 'data-show-when-disabled';
    var FIELDS = 'input, select, textarea, button';

    function parse(rule) {
        var m = rule.match(/^\s*([^!=\s]+)\s*(!?=)?\s*(.*)$/);
        if (!m) return null;
        return {
            name: m[1],
            op: m[2] || null,
            values: m[2] ? m[3].split(',').map(function (v) { return v.trim(); }) : []
        };
    }

    // The block's form, even when a portaled menu moved it out of the
    // <form> and dropdowns.js linked its fields back with form="".
    function formOf(block) {
        var form = block.closest('form');
        if (form) return form;
        var field = block.querySelector(FIELDS);
        return field ? field.form : null;
    }

    function controlsFor(block, name) {
        var form = formOf(block);
        var pool = form
            ? form.elements
            : (block.closest('.sheet, .modal, .dropdown-menu') || document).querySelectorAll(FIELDS);
        return Array.prototype.filter.call(pool, function (el) {
            return el.name === name && !block.contains(el);
        });
    }

    function valuesOf(controls) {
        var out = [];
        controls.forEach(function (el) {
            if (el.type === 'radio' || el.type === 'checkbox') {
                if (el.checked) out.push(el.value);
            } else if (el.tagName === 'SELECT' && el.multiple) {
                Array.prototype.forEach.call(el.selectedOptions, function (o) { out.push(o.value); });
            } else if (el.value !== '') {
                out.push(el.value);
            }
        });
        return out;
    }

    function inHiddenBlock(el) {
        return !!el.closest('[' + ATTR + '][hidden]');
    }

    function matches(block) {
        // Inside a hidden block, stay hidden whatever this block's own rule says.
        if (block.parentElement && inHiddenBlock(block.parentElement)) return false;
        var rule = parse(block.getAttribute(ATTR));
        if (!rule) return true;
        var controls = controlsFor(block, rule.name);
        if (!controls.length) return true;  // nothing to depend on; don't hide content
        // A controlling field inside a hidden block counts as unanswered.
        var current = controls.every(inHiddenBlock) ? [] : valuesOf(controls);
        if (!rule.op) return current.length > 0;
        var hit = current.some(function (v) { return rule.values.indexOf(v) !== -1; });
        return rule.op === '=' ? hit : !hit;
    }

    function setShown(block, shown) {
        if (shown) {
            block.removeAttribute('hidden');
            block.querySelectorAll('[' + MARK + ']').forEach(function (el) {
                el.disabled = false;
                el.removeAttribute(MARK);
            });
        } else {
            block.setAttribute('hidden', '');
            block.querySelectorAll(FIELDS).forEach(function (el) {
                if (el.disabled) return;
                el.disabled = true;
                el.setAttribute(MARK, '');
            });
        }
    }

    // A block can depend on a control inside a later block, so the pass repeats
    // until no block flips, capped at the block count in case of a cycle.
    function refresh() {
        var blocks = document.querySelectorAll('[' + ATTR + ']');
        for (var pass = 0; pass < blocks.length; pass++) {
            var changed = false;
            blocks.forEach(function (block) {
                var shown = matches(block);
                if (block.hasAttribute('hidden') === shown) changed = true;
                setShown(block, shown);
            });
            if (!changed) break;
        }
    }

    document.addEventListener('change', refresh);
    document.addEventListener('input', function (e) {
        var t = e.target;
        if (t.type === 'radio' || t.type === 'checkbox' || t.tagName === 'SELECT') return;  // change covers these
        refresh();
    });
    // These widgets set a hidden input's value without firing change.
    ['combobox:select', 'typeahead:select', 'taginput:add', 'taginput:remove'].forEach(function (name) {
        document.addEventListener(name, refresh);
    });
    // reset fires before the values revert.
    document.addEventListener('reset', function () { setTimeout(refresh, 0); });
    document.addEventListener('htmx:afterSettle', refresh);
    document.addEventListener('dropdown:open', refresh);
    // A sheet's body is inserted by script (rfSheet.render), not by an htmx swap.
    document.addEventListener('sheet:ready', refresh);

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', refresh);
    } else {
        refresh();
    }

    SeeMe.showWhen = { refresh: refresh };
})();

window.SeeMe = SeeMe;
